import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_NOTIFICATION_SETTINGS } from '../graphql/queries/notificationSettings';
import { UPDATE_NOTIFICATION_SETTINGS } from '../graphql/mutations/notificationSettings';

export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidTelegramChatId = (chatId) => {
  if (!chatId) return false;
  const cleaned = chatId.replace(/^-/, '');
  return /^\d{6,15}$/.test(cleaned);
};

// Get browser's timezone (IANA format)
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const DEFAULT_FORM_DATA = {
  telegramEnabled: false,
  telegramChatID: '',
  emailEnabled: false,
  emailAddress: '',
  exitLevelNotifications: false,
  exitNotifyApproaching: false,
  invalidationNotifications: false,
  invalidationNotifyApproaching: false,
  rapidMoversEnabled: false,
  dailyDigestEnabled: false,
  moverDustThreshold: 20,
  moverMaxPositions: 5,
  timezone: getBrowserTimezone(),
  dailyDigestHour: 8,
};

export const useNotificationSettings = ({
  portfolioId,
  skip = false,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const { data, loading, error, refetch } = useQuery(GET_NOTIFICATION_SETTINGS, {
    variables: { portfolioID: portfolioId },
    skip: !portfolioId || skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const [updateSettings, { loading: updating }] = useMutation(UPDATE_NOTIFICATION_SETTINGS, {
    onCompleted: (data) => {
      if (data.updateNotificationSettings.success) {
        toast.success('Notification settings updated!');
        try {
          const evt = new CustomEvent('notifications:updated', { detail: { portfolioId } });
          window.dispatchEvent(evt);
        } catch (_) {}
        onSuccess?.();
      } else {
        toast.error(`Failed to update settings: ${data.updateNotificationSettings.error}`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });

  const resetForm = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
  }, []);

  useEffect(() => {
    resetForm();
    if (portfolioId && !skip) {
      refetch({ portfolioID: portfolioId });
    }
  }, [portfolioId, skip, refetch, resetForm]);

  useEffect(() => {
    if (data?.notificationSettings) {
      const settings = data.notificationSettings;
      setFormData({
        telegramEnabled: settings.telegramEnabled || false,
        telegramChatID: settings.telegramChatID || '',
        emailEnabled: settings.emailEnabled || false,
        emailAddress: settings.emailAddress || '',
        exitLevelNotifications: settings.exitLevelNotifications || false,
        exitNotifyApproaching: settings.exitNotifyApproaching || false,
        invalidationNotifications: settings.invalidationNotifications || false,
        invalidationNotifyApproaching: settings.invalidationNotifyApproaching || false,
        rapidMoversEnabled: settings.rapidMoversEnabled ?? false,
        dailyDigestEnabled: settings.dailyDigestEnabled ?? false,
        moverDustThreshold: settings.moverDustThreshold ?? 20,
        moverMaxPositions: settings.moverMaxPositions ?? 5,
        timezone: settings.timezone || getBrowserTimezone(),
        dailyDigestHour: settings.dailyDigestHour ?? 8,
      });
    }
  }, [data]);

  const validation = useMemo(() => {
    const telegramValid = !formData.telegramEnabled || isValidTelegramChatId(formData.telegramChatID);
    const emailValid = !formData.emailEnabled || isValidEmail(formData.emailAddress);
    const hasValidChannel = 
      (formData.telegramEnabled && isValidTelegramChatId(formData.telegramChatID)) ||
      (formData.emailEnabled && isValidEmail(formData.emailAddress));
    
    return {
      telegramValid,
      emailValid,
      hasValidChannel,
      canEnableStrategies: hasValidChannel,
    };
  }, [formData]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      let newValue;
      if (type === 'checkbox') {
        newValue = checked;
      } else if (type === 'number') {
        newValue = typeof value === 'number' ? value : parseFloat(value);
      } else {
        newValue = value;
      }

      const newData = {
        ...prev,
        [name]: newValue,
      };
      
      if (type === 'checkbox' && !checked) {
        const hasOtherValidChannel = 
          (name !== 'telegramEnabled' && prev.telegramEnabled && isValidTelegramChatId(prev.telegramChatID)) ||
          (name !== 'emailEnabled' && prev.emailEnabled && isValidEmail(prev.emailAddress));
        
        if (!hasOtherValidChannel) {
          newData.exitLevelNotifications = false;
          newData.exitNotifyApproaching = false;
          newData.invalidationNotifications = false;
          newData.invalidationNotifyApproaching = false;
          newData.rapidMoversEnabled = false;
          newData.dailyDigestEnabled = false;
        }
      }
      
      if (name === 'exitLevelNotifications' && !checked) {
        newData.exitNotifyApproaching = false;
      }
      if (name === 'invalidationNotifications' && !checked) {
        newData.invalidationNotifyApproaching = false;
      }
      
      return newData;
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    
    if (!portfolioId) {
      toast.error('No portfolio selected');
      return;
    }
    
    if (formData.telegramEnabled && !isValidTelegramChatId(formData.telegramChatID)) {
      toast.error('Please enter a valid Telegram Chat ID (6-15 digits)');
      return;
    }
    
    if (formData.emailEnabled && !isValidEmail(formData.emailAddress)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      await updateSettings({
        variables: {
          input: {
            portfolioID: portfolioId,
            telegramEnabled: formData.telegramEnabled,
            telegramChatID: formData.telegramChatID || null,
            emailEnabled: formData.emailEnabled,
            emailAddress: formData.emailAddress || null,
            exitLevelNotifications: formData.exitLevelNotifications,
            exitNotifyApproaching: formData.exitNotifyApproaching,
            invalidationNotifications: formData.invalidationNotifications,
            invalidationNotifyApproaching: formData.invalidationNotifyApproaching,
            rapidMoversEnabled: formData.rapidMoversEnabled,
            dailyDigestEnabled: formData.dailyDigestEnabled,
            moverDustThreshold: formData.moverDustThreshold,
            moverMaxPositions: formData.moverMaxPositions,
            timezone: formData.timezone,
            dailyDigestHour: formData.dailyDigestHour,
          },
        },
      });
    } catch (error) {
      console.error('Update failed:', error);
    }
  }, [portfolioId, formData, updateSettings]);

  return {
    formData,
    setFormData,
    validation,
    loading,
    error,
    updating,
    handleInputChange,
    handleSubmit,
    refetch,
    resetForm,
  };
};

export default useNotificationSettings;
